import Analytics from '@/lib/analytics';
import { formatSwapResult, INITIAL_SWAP_AMOUNT, SwapResult } from '@/utils/jupiter';
import { useCallback } from 'react';

// Common token addresses
export const SOL_MINT_ADDRESS = 'So11111111111111111111111111111111111111112';
export const USDC_MINT_ADDRESS = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
export const BONK_MINT_ADDRESS = 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263';

// The wallet address that will be used for fees for the swap
const FEE_ACCOUNT_PUBKEY = process.env.NEXT_PUBLIC_FEE_ACCOUNT_PUBKEY;
// The fee in basis points for the swap (70 = 0.7%)
const FEE_BPS = 70;

if (!FEE_ACCOUNT_PUBKEY) {
  console.error('FEE_ACCOUNT_PUBKEY environment variable is not set!');
}

// Initialize platform fee configuration for multiple tokens
const platformFeeAndAccounts = {
  feeBps: FEE_BPS,
  feeAccounts: new Map([
    [SOL_MINT_ADDRESS, FEE_ACCOUNT_PUBKEY],
    [USDC_MINT_ADDRESS, FEE_ACCOUNT_PUBKEY],
    [BONK_MINT_ADDRESS, FEE_ACCOUNT_PUBKEY],
  ]),
};

export function useJupiterModal(outputTokenAddress: string) {
  const openJupiterModal = useCallback(() => {
    try {
      // Verify fee configuration before initializing
      if (!FEE_ACCOUNT_PUBKEY) {
        console.error('Cannot initialize Jupiter - FEE_ACCOUNT_PUBKEY is not set');
        return;
      }

      const config = {
        displayMode: 'modal' as const,
        endpoint: 'https://l7db1lpgkb.execute-api.us-east-2.amazonaws.com/prod/swap-token',
        platformFeeAndAccounts,
        strictTokenList: false,
        formProps: {
          initialAmount: INITIAL_SWAP_AMOUNT,
          initialInputMint: SOL_MINT_ADDRESS,
          initialOutputMint: outputTokenAddress,
          fixedOutputMint: true,
        },
        onSuccess: ({ swapResult }: { swapResult: SwapResult }) => {
          const formattedResult = formatSwapResult(swapResult);

          if (!formattedResult) {
            console.error('Failed to format swap result');
            return;
          }

          Analytics.trackSwapSuccess({
            inputAmount: Number(formattedResult.inputAmount),
            outputAmount: Number(formattedResult.outputAmount),
            inputToken: formattedResult.inputAddress,
            outputToken: formattedResult.outputAddress,
            inputMint: formattedResult.inputAddress,
            outputMint: formattedResult.outputAddress,
            transactionId: formattedResult.txid,
          });
        },
        onError: ({ error }: { error: any }) => {
          Analytics.trackSwapError({
            errorMessage: error?.message || 'Unknown error',
            inputToken: SOL_MINT_ADDRESS,
            outputToken: outputTokenAddress,
          });
        },
      };
      window?.Jupiter?.init(config);
    } catch (e) {
      console.error('Error initializing Jupiter:', e);
      console.error('Token address attempted:', outputTokenAddress);

      Analytics.trackInitError(e);
    }
  }, [outputTokenAddress]);

  return { openJupiterModal };
}
